import { Component, Suspense, lazy, useState } from 'react';

function ModuleState({ label, failed = false, onRetry, onBack, onClose }) {
  return (
    <section role={failed ? 'alert' : 'status'} aria-live="polite" style={{ padding: 24 }}>
      <p>{failed ? `Could not open ${label}. Try loading it again.` : `Loading ${label}…`}</p>
      {failed && (
        <button type="button" onClick={onRetry}>
          Retry loading
        </button>
      )}
      {failed && (
        <button
          type="button"
          title="Reload the application; unsaved changes will be lost"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      )}
      {onBack && (
        <button type="button" onClick={onBack}>
          Back
        </button>
      )}
      {onClose && (
        <button type="button" onClick={onClose}>
          Close
        </button>
      )}
    </section>
  );
}

class ModuleBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <ModuleState
        {...this.props}
        failed
        onRetry={() => {
          this.props.onRetry();
          this.setState({ failed: false });
        }}
      />
    );
  }
}

// Define at module scope. React.lazy caches rejections, so an explicit retry needs
// a new lazy component, while successful loads are reused when a form reopens.
export function lazyModule(load, label) {
  let cached = lazy(load);
  function DeferredModule(props) {
    const [View, setView] = useState(() => cached);
    const navigation = { label, onBack: props.onBack, onClose: props.onClose };
    return (
      <ModuleBoundary
        {...navigation}
        onRetry={() => {
          cached = lazy(load);
          setView(() => cached);
        }}
      >
        <Suspense fallback={<ModuleState {...navigation} />}>
          <View {...props} />
        </Suspense>
      </ModuleBoundary>
    );
  }
  DeferredModule.displayName = `Deferred(${label})`;
  return DeferredModule;
}
